// src/components/ui/RichTextEditor/RichTextEditor.jsx
import React, { useState, useRef, useEffect } from 'react';
import { 
  Bold, 
  Italic, 
  Underline, 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  List, 
  ListOrdered,
  Link,
  Image,
  Code,
  MoreHorizontal
} from 'lucide-react';
import styles from './RichTextEditor.module.css';

const RichTextEditor = ({ 
  value = '', 
  onChange, 
  placeholder = 'Enter description...', 
  error = null,
  className = '' 
}) => {
  const [content, setContent] = useState(value);
  const [isToolbarVisible, setIsToolbarVisible] = useState(false);
  const editorRef = useRef(null);
  const toolbarRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && content !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = content;
    }
  }, [content]);

  const handleContentChange = () => {
    if (editorRef.current) {
      const newContent = editorRef.current.innerHTML;
      setContent(newContent);
      onChange?.(newContent);
    }
  };

  const handleFocus = () => {
    setIsToolbarVisible(true);
  };

  const handleBlur = (e) => {
    // Don't hide toolbar if clicking on toolbar buttons
    if (toolbarRef.current && toolbarRef.current.contains(e.relatedTarget)) {
      return;
    }
    setTimeout(() => {
      setIsToolbarVisible(false);
    }, 150);
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleContentChange();
  };

  const isCommandActive = (command) => {
    return document.queryCommandState(command);
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const insertImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      execCommand('insertImage', url);
    }
  };

  const formatButton = (command, icon, title, value = null) => (
    <button
      type="button"
      className={`${styles.toolbarButton} ${isCommandActive(command) ? styles.active : ''}`}
      onClick={() => execCommand(command, value)}
      onMouseDown={(e) => e.preventDefault()}
      title={title}
    >
      {icon}
    </button>
  );

  return (
    <div className={`${styles.richTextEditor} ${className}`}>
      <div className={`${styles.container} ${error ? styles.error : ''} ${isToolbarVisible ? styles.focused : ''}`}>
        <div ref={toolbarRef} className={`${styles.toolbar} ${isToolbarVisible ? styles.visible : ''}`}>
          {formatButton('bold', <Bold size={16} />, 'Bold')}
          {formatButton('italic', <Italic size={16} />, 'Italic')}
          {formatButton('underline', <Underline size={16} />, 'Underline')}
          
          <div className={styles.separator} />
          
          {formatButton('justifyLeft', <AlignLeft size={16} />, 'Align Left')}
          {formatButton('justifyCenter', <AlignCenter size={16} />, 'Align Center')}
          {formatButton('justifyRight', <AlignRight size={16} />, 'Align Right')}
          
          <div className={styles.separator} />
          
          {formatButton('insertUnorderedList', <List size={16} />, 'Bullet List')}
          {formatButton('insertOrderedList', <ListOrdered size={16} />, 'Numbered List')}
          
          <div className={styles.separator} />
          
          <button
            type="button"
            className={styles.toolbarButton}
            onClick={insertLink}
            onMouseDown={(e) => e.preventDefault()}
            title="Insert Link"
          >
            <Link size={16} />
          </button>
          
          <button
            type="button"
            className={styles.toolbarButton}
            onClick={insertImage}
            onMouseDown={(e) => e.preventDefault()}
            title="Insert Image"
          >
            <Image size={16} />
          </button>
          
          {formatButton('formatBlock', <Code size={16} />, 'Code Block', 'pre')}
          
          <div className={styles.separator} />
          
          <button
            type="button"
            className={styles.toolbarButton}
            onClick={() => execCommand('removeFormat')}
            onMouseDown={(e) => e.preventDefault()}
            title="Clear Formatting"
          >
            <MoreHorizontal size={16} />
          </button>
        </div>
        
        {!content && (
          <div className={`${styles.placeholder} ${isToolbarVisible ? styles.withToolbar : ''}`}>
            {placeholder}
          </div>
        )}
        
        <div
          ref={editorRef}
          contentEditable
          onInput={handleContentChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={styles.editor}
          suppressContentEditableWarning={true}
        />
      </div>
      
      {error && <span className={styles.errorText}>{error}</span>}
    </div>
  );
};

export default RichTextEditor;